import React from 'react';
import { Link, useI18next } from 'gatsby-plugin-react-i18next';
import GatsbyLink from 'gatsby-link';
import { useLocation } from '@reach/router';
import * as classes from './LanguageSwitch.module.scss';
import clsx from 'clsx';
import { useLanguageVariants } from '../../util/i18n';
import { useDynamic } from '../../hooks/useDynamic';

export interface ILanguageSwitchProps {
    supportedLanguages?: string[];
}

const linkClass = (language: string) => `link${ language[0].toUpperCase() }${ language.substring(1) }`;

export const LanguageSwitch: React.FunctionComponent<ILanguageSwitchProps> = props => {
    const { t, languages, originalPath, language: currentLanguage } = useI18next();
    const { languageVariants } = useLanguageVariants();
    const location = useLocation();
    const supportedLanguages = props.supportedLanguages
        ?? (languageVariants.length > 0 ? languageVariants.map(lv => lv.language) : languages);

    const path = useDynamic(() => {
        return originalPath.includes('[...]')
            ? location.pathname
                ? location.pathname.replace(`/${ currentLanguage }`, '')
                : '/'
            : originalPath;
    }, [ originalPath, location.pathname, currentLanguage ]);

    const data = languageVariants.length > 0
        ? languageVariants
        : supportedLanguages.map(language => ({ language, path }));

    const LinkComponent = languageVariants.length > 0 ? GatsbyLink : Link;

    return (
        <div className={classes.container}>
            <nav aria-label={t('languageSwitch.title')}>
                <ul className={classes.list}>
                    {data.map(({ language, path }) => (
                        <li key={language} className={classes.item}>
                            <LinkComponent
                                to={path ?? '/'}
                                language={language}
                                lang={language}
                                aria-current={language === currentLanguage}
                                className={clsx(classes.link, classes[linkClass(language)], language === currentLanguage ? classes.active : undefined)}
                            >
                                {t(`languageSwitch.languages.${ language }`)}
                            </LinkComponent>
                        </li>
                    ))}
                </ul>
            </nav>
        </div>
    );
};
